import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import PostRequest from "../../../services/PostRequest";
import { showToaster } from "../../../common/common";
import "../../../styles/auth.css";

export const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const otpInputsRef = useRef([]);

  // Calculate password strength (1-4)
  const calculateStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const getStrengthLabel = (level) => {
    const labels = ["", "Weak", "Fair", "Good", "Strong"];
    return labels[level] || "";
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("new_password", value);
    setPasswordStrength(calculateStrength(value));
  };

  const handleOtpInputChange = (index, value) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) return;

    const newOtp = formik.values.otp.split("");
    newOtp[index] = value;
    formik.setFieldValue("otp", newOtp.join(""));

    // Auto-focus next input
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!formik.values.otp[index] && index > 0) {
        otpInputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePasswordReset = async (data) => {
    try {
      const option = {
        showLoader: "reset-id",
        auth: false,
      };

      const payload = {
        user: location?.state,
        password: data?.new_password,
        otp: data?.otp,
      };
      await PostRequest("/users/password/reset", payload, option);

      showToaster("Password change successfully.");
      navigate("/sign-in");
    } catch (error) {
      console.log("error", error.message);
      showToaster(error.message, "error");
    }
  };

  const validate = (values) => {
    const errors = {};

    if (!values.otp || values.otp.length !== 6) {
      errors.otp = "OTP Required";
    }

    if (!values.new_password) {
      errors.new_password = "Password Required";
    } else if (values.new_password.length < 8) {
      errors.new_password = "Password must be at least 8 characters long.";
    }

    if (!values.confirmPassword) {
      errors.confirmPassword = "Confirm Password Required";
    } else if (values.confirmPassword !== values.new_password) {
      errors.confirmPassword = "Passwords must match";
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      otp: "",
      new_password: "",
      confirmPassword: "",
    },
    validate,
    onSubmit: (data) => handlePasswordReset(data),
  });

  return (
    <div className="auth">
      {/* Form Side */}
      <div className="auth__main">
        <div className="auth__card">
          <img
            className="auth__logo auth__logo--color"
            src="/assets/brand/multicartapi-logo.png"
            alt="MultiCartAPI"
          />
          <img
            className="auth__logo auth__logo--white"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h1>Reset password</h1>
          <p className="auth__sub">Enter the code sent to your email and choose a new password.</p>

          <form className="auth__form" onSubmit={formik.handleSubmit}>
            {/* OTP Field */}
            <div className="mca-field">
              <label className="mca-label">
                Reset code <span className="req">*</span>
              </label>
              <div
                className={`mca-otp ${formik.touched.otp && formik.errors.otp ? "is-error" : ""}`}
              >
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputsRef.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    className={formik.values.otp[index] ? "is-filled" : ""}
                    value={formik.values.otp[index] || ""}
                    onChange={(e) => handleOtpInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
              {formik.touched.otp && formik.errors.otp && (
                <span className="mca-error">{formik.errors.otp}</span>
              )}
            </div>

            {/* New Password Field */}
            <div className="mca-field">
              <label className="mca-label" htmlFor="rp-pw">
                New password <span className="req">*</span>
              </label>
              <div className="pw-field">
                <input
                  className="mca-input"
                  id="rp-pw"
                  type={showPassword ? "text" : "password"}
                  placeholder="At least 8 characters"
                  value={formik.values.new_password}
                  onChange={handlePasswordChange}
                  onBlur={formik.handleBlur}
                  name="new_password"
                />
                <button
                  type="button"
                  className={`mca-iconbtn pw-field__toggle ${showPassword ? "is-revealed" : ""}`}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Show password"
                >
                  <svg
                    className="ico-on"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="2.6" />
                  </svg>
                  <svg
                    className="ico-off"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M9.4 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.2 6.2A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3-.5" />
                  </svg>
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                <div
                  className="mca-strength"
                  data-level={passwordStrength}
                  style={{ flex: 1 }}
                >
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="mca-help" style={{ minWidth: "42px" }}>
                  {getStrengthLabel(passwordStrength)}
                </span>
              </div>
              {formik.touched.new_password && formik.errors.new_password && (
                <span className="mca-error">{formik.errors.new_password}</span>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="mca-field">
              <label className="mca-label" htmlFor="rp-confirm">
                Confirm new password <span className="req">*</span>
              </label>
              <div className="pw-field">
                <input
                  className="mca-input"
                  id="rp-confirm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="confirmPassword"
                />
                <button
                  type="button"
                  className={`mca-iconbtn pw-field__toggle ${showConfirm ? "is-revealed" : ""}`}
                  onClick={() => setShowConfirm(!showConfirm)}
                  aria-label="Show password"
                >
                  <svg
                    className="ico-on"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="2.6" />
                  </svg>
                  <svg
                    className="ico-off"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M9.4 5.2A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.2 6.2A17 17 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3-.5" />
                  </svg>
                </button>
              </div>
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <span className="mca-error">{formik.errors.confirmPassword}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              className="mca-btn mca-btn--primary"
              id="reset-id"
              type="submit"
              style={{ width: "100%", marginTop: "4px" }}
              disabled={formik.isSubmitting}
            >
              <span
                className="mca-btn__spin"
                style={{ display: formik.isSubmitting ? "inline-flex" : "none" }}
              >
                <span className="mca-spinner"></span>
              </span>
              <span className="mca-btn__label">
                {formik.isSubmitting ? "Resetting…" : "Reset password"}
              </span>
            </button>
          </form>

          <a
            className="auth__back"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/sign-in");
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 6-6 6 6 6" />
            </svg>
            Back to sign in
          </a>
        </div>
      </div>

      {/* Brand Side */}
      <div className="auth__brand">
        <div>
          <img
            className="auth__brand-logo"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h2>Set a new password.</h2>
          <p>Choose a strong password — we recommend a mix of letters, numbers, and symbols.</p>
        </div>
        <ul className="auth__feat">
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            8+ characters recommended
          </li>
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            Strength meter as you type
          </li>
          <li>
            <span className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            Takes effect immediately
          </li>
        </ul>
        <p className="auth__brand-foot">MultiCartAPI — Next-Generation Commerce Platform</p>
      </div>
    </div>
  );
};
